'use client';
import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import CategoryFilter from './components/CategoryFilter';
import PhotoGrid from './components/PhotoGrid';
import Lightbox from './components/Lightbox';
import { galleryService, GalleryPhoto } from '@/lib/services/galleryService';

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [allPhotos, setAllPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPhotos() {
      try {
        setLoading(true);
        const photos = await galleryService.getPublicPhotos();
        setAllPhotos(photos);
      } catch (error) {
        console.error('Error loading gallery photos:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchPhotos();
  }, []);

  // Derive categories dynamically from loaded photos
  const categories = [
    'All',
    ...Array.from(new Set(allPhotos.map((p) => p.category))).sort(),
  ];

  const filteredPhotos =
    activeCategory === 'All'
      ? allPhotos
      : allPhotos.filter((photo) => photo.category === activeCategory);

  const handlePhotoClick = (index: number) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const handleNext = () => {
    setCurrentPhotoIndex((prev) => (prev < filteredPhotos.length - 1 ? prev + 1 : prev));
  };

  const handlePrev = () => {
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );

    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, allPhotos.length, activeCategory]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-16 reveal">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight">
              Portfolio
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A collection of moments captured through cinematic storytelling
            </p>
          </div>

          {/* Category Filter — only show once photos are loaded and there are multiple galleries */}
          {!loading && categories.length > 1 && (
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={(cat) => {
                setActiveCategory(cat);
                setCurrentPhotoIndex(0);
              }}
            />
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-20">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-white border-r-transparent"></div>
              <p className="text-muted-foreground mt-4">Loading photos...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && allPhotos.length === 0 && (
            <div className="text-center py-20">
              <p className="text-muted-foreground">No photos available yet.</p>
            </div>
          )}

          {/* Photo Grid */}
          {!loading && allPhotos.length > 0 && (
            <div className="reveal delay-200">
              <PhotoGrid photos={filteredPhotos} onPhotoClick={handlePhotoClick} />
            </div>
          )}

          {/* Photo Count */}
          {!loading && allPhotos.length > 0 && (
            <div className="text-center mt-12 reveal delay-300">
              <p className="text-sm text-muted-foreground">
                Showing {filteredPhotos.length} photo{filteredPhotos.length !== 1 ? 's' : ''}
                {activeCategory !== 'All' && ` in ${activeCategory}`}
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {lightboxOpen && (
        <Lightbox
          photos={filteredPhotos}
          currentIndex={currentPhotoIndex}
          onClose={() => setLightboxOpen(false)}
          onNext={handleNext}
          onPrev={handlePrev}
        />
      )}
    </>
  );
}
